import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RootState from "../../../redux/states/root.state";
import NewsReader from "./NewsReader";
import { CalendarIcon, ClockIcon, GiftIcon } from "../../../components/common/Icons/Icons";

const daysLeftOf = (promo: any) => promo.end_date
  ? Math.ceil((new Date(promo.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  : null;

const shortDate = (value: string) => new Date(value).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
const longDate = (value: string) => new Date(value).toLocaleDateString(undefined, {month: 'long', day: 'numeric', year: 'numeric'});

const daysLeftText = (days: number) => days <= 0 ? 'Ends today' : days === 1 ? '1 day left' : `${days} days left`;

const PromosList: React.FC = () => {
  const { data: promos, loading, meta } = useSelector((state: RootState) => state.promos);
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const handlePageChange = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
    setActiveTab(0);
  };

  // List: "2d left" (amber) in the last three days, otherwise the end date
  const listAside = (promo: any) => {
    const daysLeft = daysLeftOf(promo);
    if (daysLeft === null) return null;
    return daysLeft <= 3
      ? <span className="nw-soon">{daysLeft <= 0 ? 'Today' : `${daysLeft}d left`}</span>
      : shortDate(promo.end_date);
  };

  // Open promo: the time left (more urgent in colour as it gets closer), then the end date
  const detailMeta = (promo: any) => {
    const daysLeft = daysLeftOf(promo);
    if (daysLeft === null) return <span className="nw-meta-text"><CalendarIcon/> No end date</span>;
    const tone = daysLeft <= 3 ? 'bad' : daysLeft <= 7 ? 'caution' : daysLeft <= 14 ? 'info' : 'neutral';
    return (
      <>
        <span className={`dt-pill nw-pill ${tone}`}><ClockIcon/>{daysLeftText(daysLeft)}</span>
        <span className="nw-meta-text"><CalendarIcon/> Ends {longDate(promo.end_date)}</span>
      </>
    );
  };

  return (
    <NewsReader
      title="Promos"
      total={meta?.total ?? promos.length}
      items={promos}
      loading={loading}
      emptyText="No promos at this time."
      activeIndex={activeTab}
      onSelect={setActiveTab}
      page={searchParams.get("page") ? Number(searchParams.get("page")) : 1}
      totalPages={meta?.total_pages ?? 1}
      onPage={handlePageChange}
      icon={<GiftIcon/>}
      bodyOf={(promo) => promo.body}
      listAside={listAside}
      detailMeta={detailMeta}
      footerNote={(promo) => promo.end_date && <><CalendarIcon/> Offer ends {longDate(promo.end_date)}</>}
      ctaLabel={(promo) => promo.button_label || 'Learn More'}
      kind="Promo"
    />
  );
};

export default PromosList;
